import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AccrualType, VacationPolicy } from '../../types';
import { CreateVacationPolicyDto } from '../../services';

interface PolicyFormDialogProps {
  policy?: VacationPolicy | null;
  open: boolean;
  isSubmitting?: boolean;
  serverError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateVacationPolicyDto) => void;
}

interface PolicyFormState {
  countryCode: string;
  name: string;
  annualAllowance: string;
  accrualType: AccrualType;
  carryOverLimit: string;
  maxConsecutiveDays: string;
  minConsecutiveDays: string;
  advanceNoticeDays: string;
  probationPeriodDays: string;
}

const emptyForm: PolicyFormState = {
  countryCode: '',
  name: '',
  annualAllowance: '',
  accrualType: AccrualType.YEARLY,
  carryOverLimit: '',
  maxConsecutiveDays: '',
  minConsecutiveDays: '',
  advanceNoticeDays: '',
  probationPeriodDays: '',
};

const toState = (policy: VacationPolicy): PolicyFormState => ({
  countryCode: policy.countryCode,
  name: policy.name,
  annualAllowance: String(policy.annualAllowance),
  accrualType: policy.accrualType,
  carryOverLimit: String(policy.carryOverLimit ?? ''),
  maxConsecutiveDays: policy.maxConsecutiveDays != null ? String(policy.maxConsecutiveDays) : '',
  minConsecutiveDays: String(policy.minConsecutiveDays ?? ''),
  advanceNoticeDays: String(policy.advanceNoticeDays ?? ''),
  probationPeriodDays: String(policy.probationPeriodDays ?? ''),
});

const optionalNumber = (value: string): number | undefined =>
  value.trim() === '' ? undefined : Number(value);

export const PolicyFormDialog: React.FC<PolicyFormDialogProps> = ({
  policy,
  open,
  isSubmitting,
  serverError,
  onOpenChange,
  onSubmit,
}) => {
  const { t } = useTranslation('vacations');
  const [form, setForm] = useState<PolicyFormState>(emptyForm);
  const [error, setError] = useState('');
  const isEdit = !!policy;

  useEffect(() => {
    if (open) {
      setForm(policy ? toState(policy) : emptyForm);
      setError('');
    }
  }, [open, policy]);

  const update = <K extends keyof PolicyFormState>(key: K, value: PolicyFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const isFlexUnlimited =
    form.accrualType === AccrualType.FLEX &&
    (form.annualAllowance.trim() === '' || Number(form.annualAllowance) <= 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.countryCode.trim().length !== 2) {
      setError(t('admin.policies.error.countryCode'));
      return;
    }
    if (!form.name.trim()) {
      setError(t('admin.policies.error.name'));
      return;
    }
    if (form.annualAllowance.trim() === '' || Number(form.annualAllowance) < 0) {
      setError(t('admin.policies.error.allowance'));
      return;
    }
    setError('');
    onSubmit({
      countryCode: form.countryCode.trim().toUpperCase(),
      name: form.name.trim(),
      annualAllowance: Number(form.annualAllowance),
      accrualType: form.accrualType,
      carryOverLimit: optionalNumber(form.carryOverLimit),
      maxConsecutiveDays: optionalNumber(form.maxConsecutiveDays),
      minConsecutiveDays: optionalNumber(form.minConsecutiveDays),
      advanceNoticeDays: optionalNumber(form.advanceNoticeDays),
      probationPeriodDays: optionalNumber(form.probationPeriodDays),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="policy-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('admin.policies.editTitle') : t('admin.policies.createTitle')}</DialogTitle>
          <DialogDescription>{t('admin.policies.formDescription')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {(error || serverError) && (
            <Alert variant="destructive" data-testid="policy-form-error">
              <AlertDescription>{error || serverError}</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="policy-country">{t('admin.policies.fields.countryCode')}</Label>
              <Input
                id="policy-country"
                value={form.countryCode}
                maxLength={2}
                disabled={isEdit}
                onChange={(e) => update('countryCode', e.target.value.toUpperCase())}
                placeholder="US"
                data-testid="policy-country-code"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="policy-name">{t('admin.policies.fields.name')}</Label>
              <Input
                id="policy-name"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                data-testid="policy-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="policy-allowance">{t('admin.policies.fields.annualAllowance')}</Label>
              <Input
                id="policy-allowance"
                type="number"
                min={0}
                max={365}
                value={form.annualAllowance}
                onChange={(e) => update('annualAllowance', e.target.value)}
                data-testid="policy-annual-allowance"
              />
              {isFlexUnlimited && (
                <Alert data-testid="policy-flex-unlimited-hint">
                  <AlertDescription>{t('admin.policies.flexUnlimitedHint')}</AlertDescription>
                </Alert>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{t('admin.policies.fields.accrualType')}</Label>
              <Select
                value={form.accrualType}
                onValueChange={(value) => update('accrualType', value as AccrualType)}
              >
                <SelectTrigger data-testid="policy-accrual-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(AccrualType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`accrualType.${type.toLowerCase()}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="policy-carryover">{t('admin.policies.fields.carryOverLimit')}</Label>
              <Input
                id="policy-carryover"
                type="number"
                min={0}
                value={form.carryOverLimit}
                onChange={(e) => update('carryOverLimit', e.target.value)}
                data-testid="policy-carry-over-limit"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="policy-max">{t('admin.policies.fields.maxConsecutiveDays')}</Label>
              <Input
                id="policy-max"
                type="number"
                min={1}
                value={form.maxConsecutiveDays}
                onChange={(e) => update('maxConsecutiveDays', e.target.value)}
                data-testid="policy-max-consecutive-days"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="policy-min">{t('admin.policies.fields.minConsecutiveDays')}</Label>
              <Input
                id="policy-min"
                type="number"
                min={1}
                value={form.minConsecutiveDays}
                onChange={(e) => update('minConsecutiveDays', e.target.value)}
                data-testid="policy-min-consecutive-days"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="policy-notice">{t('admin.policies.fields.advanceNoticeDays')}</Label>
              <Input
                id="policy-notice"
                type="number"
                min={0}
                value={form.advanceNoticeDays}
                onChange={(e) => update('advanceNoticeDays', e.target.value)}
                data-testid="policy-advance-notice-days"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="policy-probation">{t('admin.policies.fields.probationPeriodDays')}</Label>
              <Input
                id="policy-probation"
                type="number"
                min={0}
                value={form.probationPeriodDays}
                onChange={(e) => update('probationPeriodDays', e.target.value)}
                data-testid="policy-probation-period-days"
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              {t('form.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting} data-testid="policy-form-submit">
              {isEdit ? t('form.save') : t('form.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
